import React, { useLayoutEffect, useRef, useState } from 'react';

import {
  type AdobeAn,
  type ComponentLibrary,
  Composition,
  fixDynamicText,
} from '@suntex.classic/adobe-animate';
import { LogLevel, Logger } from '@suntex.core/logging';
import { ErrorBoundary } from '@suntex.core/ui';

const MOUSE_OVER_CHECK_FREQUENCY = 50;

const logger = Logger.for(['AnimationCanvas'], { level: LogLevel.Trace });

export interface AnimationCanvasProps {
  width: number;
  height: number;
  compositionId: string;
  dynamicTextConfig?: { yOffset?: number };
  deps?: unknown[];
  initGraphics: (cjs: typeof createjs, an: AdobeAn) => void;
  onReady: (params: { stage: createjs.Stage; lib: ComponentLibrary }) => void;
  transformImageSource?: (source: string) => string;
}

/*
 * Note: change detection is disabled for all but width, height, and compositionId
 */

function AnimationCanvas_({
  width,
  height,
  compositionId,
  dynamicTextConfig,
  initGraphics,
  onReady,
  transformImageSource,
}: AnimationCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationContainerRef = useRef<HTMLDivElement | null>(null);
  const coverlayContainerRef = useRef<HTMLDivElement | null>(null);
  const [assetsLoaded, setAssetsLoaded] = useState(false);
  const [composition, setComposition] = useState<Composition>();
  const [AdobeAn, setAdobeAn] = useState<AdobeAn>({} as AdobeAn);

  useLayoutEffect(() => {
    logger.debug('Initializing...', { width, height, compositionId });
    fixDynamicText(dynamicTextConfig);
    initGraphics(createjs, AdobeAn);
    const composition = AdobeAn.getComposition(compositionId)!;
    setAdobeAn(AdobeAn);
    setComposition(composition);

    loadAssets(
      {
        composition,
        transformImageSource,
      },
      () => {
        setAssetsLoaded(true);
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    if (composition && assetsLoaded) {
      logger.debug('Assets loaded.', { compositionId });
      const lib = composition.getLibrary();
      const canvas = canvasRef.current!;
      const animationContainer = animationContainerRef.current!;
      const overlayContainer = coverlayContainerRef.current!;

      const stage = new lib.Stage(canvas);

      AdobeAn.makeResponsive(
        true,
        'both',
        false,
        1,
        [canvas, animationContainer, overlayContainer],
        stage,
      );
      AdobeAn.compositionLoaded(lib.properties.id);
      createjs.Ticker.framerate = lib.properties.fps;
      createjs.Ticker.addEventListener('tick', stage);
      stage.enableMouseOver(MOUSE_OVER_CHECK_FREQUENCY);
      onReady({ lib, stage });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetsLoaded, composition]);

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '0px',
        position: 'relative',
        textAlign: 'left',
      }}
    >
      <div
        ref={animationContainerRef}
        style={{
          width,
          height,
          position: 'relative',
        }}
      >
        <canvas
          ref={canvasRef}
          style={{
            position: 'absolute',
            display: 'block',
            width,
            height,
          }}
        ></canvas>
        <div
          ref={coverlayContainerRef}
          style={{
            pointerEvents: 'none',
            overflow: 'hidden',
            width,
            height,
            position: 'absolute',
            left: '0px',
            top: '0px',
            display: 'block',
          }}
        ></div>
      </div>
    </div>
  );
}

const AnimationCanvasMemo = React.memo(
  AnimationCanvas_,
  (prevProps, nextProps) => {
    return prevProps.width === nextProps.width &&
      prevProps.height === nextProps.height &&
      prevProps.compositionId === nextProps.compositionId &&
      prevProps.deps
      ? prevProps.deps.every(
          (value, index) => value === nextProps.deps?.[index],
        )
      : true;
  },
);

export const AnimationCanvas = (props: AnimationCanvasProps) => (
  <ErrorBoundary>
    <AnimationCanvasMemo {...props} />
  </ErrorBoundary>
);

type LoaderEvent = {
  target: createjs.LoadQueue;
  result?: object;
  data?: Partial<createjs.LoadItem>;
  item?: createjs.LoadItem;
};

type Metadata = {
  name: string;
  frames: unknown;
};

function loadAssets(
  {
    composition,
    transformImageSource,
  }: {
    composition: Composition;
    transformImageSource?: (src: string) => string;
  },
  onAssetsLoaded: () => void,
) {
  const lib = composition.getLibrary();

  // Load sprite sheet images before creating the badge
  if (lib.properties.manifest.length > 0) {
    transformAndLoadManifest();
  } else {
    onAssetsLoaded();
  }

  function transformAndLoadManifest() {
    // Patch up the manifest object src property to map the Animate format to our site directory structure
    if (transformImageSource)
      for (const obj of lib.properties.manifest) {
        if (obj.src.startsWith('images')) {
          obj.src = transformImageSource(obj.src);
        }
      }

    const loader = new createjs.LoadQueue(false);

    loader.addEventListener('fileload', (evt: object) => {
      handleFileLoad(evt as LoaderEvent, composition);
    });

    loader.addEventListener('error', (evt: object) => {
      handleLoadError(evt as LoaderEvent, composition);
    });

    loader.addEventListener('complete', () => {
      handleComplete(composition);
    });

    for (const item of lib.properties.manifest) {
      item.crossOrigin = true;
    }

    loader.loadManifest(lib.properties.manifest);
  }

  function handleFileLoad(evt: LoaderEvent, composition: Composition) {
    const images = composition.getImages();
    if (evt.item?.type === 'image') {
      images[evt.item.id] = evt.result!;
    }
  }

  function handleLoadError(evt: LoaderEvent, composition: Composition) {
    logger.error(
      'Failed to load file',
      {
        id: evt.data?.id,
      },
      evt,
    );
    const images = composition.getImages();
    if (evt.data?.type === 'image' && evt.data.id) {
      // fallback to transparent placeholder
      images[evt.data.id] =
        'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
    }
  }

  function handleComplete(composition: Composition) {
    // Create sprite sheets from loaded images
    const spriteSheet = composition.getSpriteSheet();
    const ssMetadata = (lib.ssMetadata ?? []).filter((m) => !!m) as Metadata[];

    for (const metadata of ssMetadata) {
      spriteSheet[metadata.name] = new createjs.SpriteSheet({
        images: [composition.getImages()[metadata.name]],
        frames: metadata.frames,
      });
    }

    // Mark assets as loaded to trigger render effect
    onAssetsLoaded();
  }
}
